/**
 * Zod schemas for everything that gets persisted, plus the migration chain.
 *
 * Rules that keep this safe across two devices on different deploys:
 *  - Every document carries `schemaVersion`.
 *  - Loading an OLDER document runs migrations forward.
 *  - Loading a NEWER document is refused, never coerced. Silently down-parsing
 *    a document written by a newer build drops the fields it doesn't know about
 *    and then writes that loss back to OneDrive.
 */

import { z } from 'zod';

/**
 * v1 -> v2: added `Transaction.updatedAt`, needed to reconcile the same
 * transaction edited on two devices.
 * v2 -> v3: added `tombstones` to both documents, so a deletion survives a
 * merge instead of being re-added by the other device's copy.
 * v3 -> v4: added the Israel/abroad classification to cards and accounts.
 * v4 -> v5: per-record config timestamps for deterministic cross-device edits.
 */
export const SCHEMA_VERSION = 5;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const isoDateTime = z.string().min(1);

export const personSchema = z.object({
  updatedAt: isoDateTime.default('1970-01-01T00:00:00.000Z'),
  id: z.string().min(1),
  displayName: z.string().min(1),
  statementAliases: z.array(z.string()),
  color: z.string(),
});

export const cardSchema = z.object({
  updatedAt: isoDateTime.default('1970-01-01T00:00:00.000Z'),
  id: z.string().min(1),
  personId: z.string().min(1),
  issuer: z.enum(['isracard', 'cal', 'max', 'amex-il', 'hapoalim', 'bank-of-cyprus', 'manual']),
  last4: z.string(),
  nickname: z.string(),
  productName: z.string().nullable(),
  region: z.enum(['israel', 'abroad']),
});

export const installmentInfoSchema = z.object({
  current: z.number().int().positive(),
  total: z.number().int().positive(),
  totalAmount: z.number(),
});

export const transactionSchema = z.object({
  id: z.string().min(1),
  dedupeKey: z.string().min(1),
  personId: z.string().min(1),
  cardId: z.string().min(1),
  transactionDate: isoDate,
  chargeDate: isoDate.nullable(),
  merchantRaw: z.string(),
  merchantNormalized: z.string(),
  amountILS: z.number(),
  originalAmount: z.number().nullable(),
  originalCurrency: z.string().nullable(),
  categoryId: z.string().nullable(),
  subcategoryId: z.string().nullable(),
  notes: z.string().nullable(),
  tags: z.array(z.string()),
  installment: installmentInfoSchema.nullable(),
  isRefund: z.boolean(),
  isStandingOrder: z.boolean(),
  excludedFromReports: z.boolean(),
  reference: z.string().nullable(),
  extraDetails: z.string().nullable(),
  sourceFileId: z.string().min(1),
  importedAt: isoDateTime,
  updatedAt: isoDateTime,
  manuallyEdited: z.boolean(),
});

export const statementSchema = z.object({
  id: z.string().min(1),
  filename: z.string(),
  fileHash: z.string(),
  issuer: cardSchema.shape.issuer,
  cardId: z.string(),
  personId: z.string(),
  periodFrom: isoDate.nullable(),
  periodTo: isoDate.nullable(),
  chargeDate: isoDate.nullable(),
  rowCount: z.number().int().nonnegative(),
  importedCount: z.number().int().nonnegative(),
  duplicateCount: z.number().int().nonnegative(),
  reviewCount: z.number().int().nonnegative(),
  unparsedCount: z.number().int().nonnegative(),
  importedAt: isoDateTime,
  statedTotal: z.number().nullable(),
});

export const categorySchema = z.object({
  updatedAt: isoDateTime.default('1970-01-01T00:00:00.000Z'),
  id: z.string().min(1),
  parentId: z.string().nullable(),
  labelHe: z.string(),
  labelEn: z.string(),
  color: z.string(),
  kind: z.enum(['fixed', 'variable']),
  need: z.enum(['essential', 'discretionary']),
  order: z.number(),
  builtin: z.boolean(),
});

export const ruleSchema = z.object({
  updatedAt: isoDateTime.default('1970-01-01T00:00:00.000Z'),
  id: z.string().min(1),
  order: z.number(),
  enabled: z.boolean(),
  name: z.string(),
  match: z.object({
    merchant: z
      .object({
        mode: z.enum(['substring', 'regex']),
        value: z.string(),
        caseSensitive: z.boolean().optional(),
      })
      .optional(),
    amount: z.object({ min: z.number().optional(), max: z.number().optional() }).optional(),
    cardId: z.string().optional(),
    currency: z.string().optional(),
    detailsContains: z.string().optional(),
  }),
  assign: z.object({
    categoryId: z.string(),
    subcategoryId: z.string().optional(),
    tags: z.array(z.string()).optional(),
  }),
});

export const budgetSchema = z.object({
  updatedAt: isoDateTime.default('1970-01-01T00:00:00.000Z'),
  id: z.string().min(1),
  categoryId: z.string(),
  monthlyAmount: z.number(),
  personId: z.string().nullable(),
});

export const settingsSchema = z.object({
  updatedAt: isoDateTime.default('1970-01-01T00:00:00.000Z'),
  amountBasis: z.enum(['cashflow', 'purchase']),
  dateBasis: z.enum(['transaction', 'charge']),
  refundMode: z.enum(['net', 'exclude', 'separate']),
  locale: z.literal('he-IL'),
});

export const tombstoneSchema = z.object({
  collection: z.enum([
    'people',
    'cards',
    'categories',
    'rules',
    'transactions',
    'statements',
  ]),
  id: z.string().min(1),
  deletedAt: isoDateTime,
});

/**
 * The small, frequently-rewritten document. Kept separate from transactions so
 * that renaming a category does not rewrite the entire spend history — see
 * plan.md §5.2.
 *
 * Each document carries the tombstones for the collections it owns, so a
 * deleted rule travels with the rules rather than in a third document that
 * could arrive out of step with them.
 */
export const configDocumentSchema = z.object({
  schemaVersion: z.number().int().positive(),
  updatedAt: isoDateTime,
  people: z.array(personSchema),
  cards: z.array(cardSchema),
  categories: z.array(categorySchema),
  rules: z.array(ruleSchema),
  budgets: z.array(budgetSchema),
  settings: settingsSchema,
  tombstones: z.array(tombstoneSchema),
});

/** The large, append-mostly document. */
export const dataDocumentSchema = z.object({
  schemaVersion: z.number().int().positive(),
  updatedAt: isoDateTime,
  transactions: z.array(transactionSchema),
  statements: z.array(statementSchema),
  tombstones: z.array(tombstoneSchema),
});

export type ConfigDocument = z.infer<typeof configDocumentSchema>;
export type DataDocument = z.infer<typeof dataDocumentSchema>;

type Migration = (doc: Record<string, unknown>) => Record<string, unknown>;

/**
 * Keyed by the version being migrated FROM. To add version 2: write
 * `2: (doc) => ...` here, bump SCHEMA_VERSION, and add the field to the schema
 * in the same commit.
 */
/**
 * A document written before tombstones existed records no deletions, which is
 * the correct reading of it: whatever was deleted on that build was already
 * lost to the union. An empty list is the honest starting point, not a guess.
 */
const addTombstones: Migration = (doc) => ({ ...doc, tombstones: [] });

/** Existing sources behaved as Israel-only before the split existed. */
const addCardRegion: Migration = (doc) => ({
  ...doc,
  cards: Array.isArray(doc.cards)
    ? doc.cards.map((row) => {
        const card = row as Record<string, unknown>;
        return card.region === undefined ? { ...card, region: 'israel' } : card;
      })
    : [],
});

export const configMigrations: Record<number, Migration> = {
  // No config-shape changes in v1 -> v2; v1 documents are already v2-shaped.
  1: (doc) => doc,
  2: addTombstones,
  3: addCardRegion,
  // Unknown legacy per-record times start below every real modification.
  4: (doc) => {
    const migrated = { ...doc };
    for (const key of ['people', 'cards', 'categories', 'rules', 'budgets']) {
      migrated[key] = Array.isArray(doc[key]) ? doc[key].map((row: Record<string, unknown>) => ({
        ...row, updatedAt: row.updatedAt ?? '1970-01-01T00:00:00.000Z',
      })) : [];
    }
    migrated.settings = { ...(doc.settings as object), updatedAt: (doc.settings as Record<string, unknown>)?.updatedAt ?? '1970-01-01T00:00:00.000Z' };
    return migrated;
  },
};

export const dataMigrations: Record<number, Migration> = {
  /**
   * v1 -> v2. Rows written before `updatedAt` existed have never been edited
   * since import, so their import time is the correct last-modified stamp.
   */
  1: (doc) => {
    const transactions = Array.isArray(doc.transactions) ? doc.transactions : [];
    return {
      ...doc,
      transactions: transactions.map((row) => {
        const tx = row as Record<string, unknown>;
        return tx.updatedAt === undefined ? { ...tx, updatedAt: tx.importedAt } : tx;
      }),
    };
  },
  2: addTombstones,
  // This document has no cards, but the chain must have an explicit step.
  3: (doc) => doc,
  4: (doc) => doc,
};

export class FutureSchemaError extends Error {
  constructor(
    readonly found: number,
    readonly supported: number,
  ) {
    super(
      `This data was written by a newer version of the app (schema v${found}, ` +
        `this build supports v${supported}). Refresh to update — continuing ` +
        `would discard the fields this build does not know about.`,
    );
    this.name = 'FutureSchemaError';
  }
}

function runMigrations(
  raw: unknown,
  migrations: Record<number, Migration>,
): Record<string, unknown> {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('Persisted document is not an object');
  }
  let doc = raw as Record<string, unknown>;
  let version = typeof doc.schemaVersion === 'number' ? doc.schemaVersion : 0;

  if (version > SCHEMA_VERSION) throw new FutureSchemaError(version, SCHEMA_VERSION);

  while (version < SCHEMA_VERSION) {
    const migrate = migrations[version];
    if (!migrate) {
      throw new Error(`No migration path from schema v${version} to v${SCHEMA_VERSION}`);
    }
    doc = migrate(doc);
    version += 1;
    doc.schemaVersion = version;
  }
  return doc;
}

export function parseConfigDocument(raw: unknown): ConfigDocument {
  return configDocumentSchema.parse(runMigrations(raw, configMigrations));
}

export function parseDataDocument(raw: unknown): DataDocument {
  return dataDocumentSchema.parse(runMigrations(raw, dataMigrations));
}
